// PRD §15.x — Option-B iOS conversation viewer embed.
//
// Route: /embed/conversation/:conversationId/viewer
//
// The B-vs-A decision gate (docs/design-ios-conversation-viewer.md §4 / §8):
// instead of a greenfield native SwiftUI viewer, reuse the proven desktop
// React components inside the existing iOS WKWebView embed — but close the
// two memory gaps that make a long conversation balloon RSS:
//
//   1. TRANSCRIPT → the desktop `CleanedTranscriptView` renders every turn
//      into the DOM at once. Here we reuse its EXACT flattening + sentence
//      sticky-bracketing sync (`flattenCleanedTurns` + `findActiveTurnIdx`)
//      and its EXACT row renderer (`CleanedTurn` / `SourceMarker`), but mount
//      the rows inside a `react-window` virtualizer so only the visible
//      window is in the DOM. The turn/sentence is the row unit.
//
//   2. AUDIO → the browser's native ranged `<audio>` element already streams
//      with bounded memory (it Range-fetches + evicts). The desktop
//      `ConversationAudioPlayer` already drives WaveSurfer's MediaElement
//      backend off server `/audio/peaks` + the ranged `/audio` endpoint, so
//      the full WAV is never materialized. We mount it as-is.
//
// CLEANED-only — no RAW/TAGGED tabs (iOS shows the consumer transcript).
//
// ── M3: PROGRESSIVE / LIVE APPEND (docs §8 M3) ───────────────────────────────
// For an IN-PROGRESS conversation the viewer extends itself as the transcript
// grows, WITHOUT reloading or losing scroll position:
//
//   • TRIGGER (two paths, both built — see docs §5b / DRIFT-8):
//       - SSE (preferred): the embed HAS event access — `EmbedLiveTranscriptView`
//         already proves the pattern. We fetch the conversation once to learn
//         its `thread_id` + `recording_state`, subscribe to the per-thread
//         `/threads/{thread_id}/events` stream, and react to a
//         `cleaned_transcript_ready` event SCOPED to THIS conversation_id.
//       - POLL (fallback): while the conversation is non-terminal we also poll
//         the CLEANED items + manifest + peaks every POLL_INTERVAL_MS. This is
//         resilient to the live SSE 500'ing server-side until DRIFT-8 ships
//         (`event_bus._Subscription` unhashable). Polling stops once the
//         conversation reaches a terminal state (ENDED + analysis committed).
//     Both paths converge on the SAME `refreshLiveData()`.
//
//   • MERGE: `refreshLiveData()` re-fetches CLEANED detail_items (+ manifest +
//     peaks) and replaces `items`. `flattenCleanedTurns` re-flattens; because
//     it sorts by detail-item `index`, existing turns keep STABLE positional
//     indices and only NEW turns land at the end. Re-fetching the full set is
//     its own dedupe (the server response is the source of truth). The peaks +
//     `total_duration_ms` are handed to the player as a `liveExtend` payload
//     that extends the waveform NON-destructively (no teardown → head stays
//     correct as duration grows).
//
//   • SCROLL: react-window tracks scroll by PIXEL offset, so appending rows at
//     the end never shifts existing rows → scroll position is preserved for
//     free. We additionally FOLLOW THE LIVE EDGE only when the user is already
//     at/near the bottom (chat-log "stick to bottom unless scrolled up"); if
//     they've scrolled up to read, we leave them put.
//
//   • `?profile=1` mode is a STATIC fixture — no live updates (no SSE, no poll).
//
// AUTH: inside the WKWebView the bearer is already stamped on the request by
// the native `EthosphereWebView` (and `EmbedAuthProvider` seeds the axios
// header from `window.__ETHOSPHERE_TOKEN__`). The ranged `<audio>` + the
// detail_items / manifest / peaks fetches inherit it. The SSE EventSource gets
// the token as `?access_token=` (EventSource can't set headers — same
// convention `EmbedLiveTranscriptView` uses). We do NOT append `?access_token=`
// on the webview <audio> path.
//
// PROFILE MODE: `?profile=1` swaps the network fetches for a large synthetic
// CLEANED fixture (~40 min / ~1800 turns) + a looped local sample WAV, so a
// human can run the M0.3 WKWebView RSS profile without seeding a real
// 40-min bulk-import. See profileFixture.js + the PROFILE PROTOCOL in the PR.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { VariableSizeList } from "react-window";
import EmbedLayout from "./EmbedLayout";
import ConversationAudioPlayer from "../ConversationAudioPlayer";
import {
  useCleanedTurns,
  CleanedTurn,
  SourceMarker,
} from "../CleanedTranscriptView";
import {
  getConversationDetailItems,
  getConversation,
  getConversationAudioManifest,
  getConversationAudioPeaks,
  buildThreadEventsUrl,
} from "../../services/embed";
import {
  currentEmbedToken,
  TOKEN_REFRESHED_EVENT,
} from "../../EmbedAuthProvider";
import {
  buildProfileFixture,
  PROFILE_AUDIO_START_EPOCH_MS,
} from "./profileFixture";

// Pause auto-scroll for this long after the user manually scrolls — same
// constant the desktop CleanedTranscriptView uses, so the feel matches.
const SCROLL_PAUSE_AFTER_USER_INTERACTION_MS = 5000;

// Looped sample WAV used in `?profile=1` mode. Public assets are served at
// the bundle root by CRA; this exercises the ranged-<audio> + waveform path
// without a real assembled-WAV endpoint. (Drop any short WAV at
// fe/public/profile-sample.wav, or point PROFILE_SAMPLE_AUDIO_URL elsewhere.)
const PROFILE_SAMPLE_AUDIO_URL = "/profile-sample.wav";

// M3 polling cadence for an in-progress conversation when (or in addition to
// when) SSE is unavailable. 12s sits in the docs §8 "every 10-15s" band.
const POLL_INTERVAL_MS = 12000;

// "Near the bottom" threshold (px) for the stick-to-live-edge behavior. If the
// user is within this many px of the end when new turns append, we follow the
// live edge; otherwise we leave them where they are.
const STICK_TO_BOTTOM_THRESHOLD_PX = 120;

// SSE events that can mean "new CLEANED transcript / audio is available for
// this conversation." Keep the historic stage events for compatibility, and
// also listen for the canonical thread-event spine names emitted by workflows.
// `recording_state_changed` / `recording.finished` re-evaluate the terminal
// stop condition.
const LIVE_SSE_EVENTS = [
  "cleaned_transcript_ready",
  "recording.transcript_cleaned",
  "recording_state_changed",
  "recording.finished",
];

// EventSource factory pulled out so tests can substitute a fake (mirrors
// EmbedLiveTranscriptView). Only references `window` (jsdom + every browser).
const defaultEventSourceFactory = (url) => new window.EventSource(url);

// A conversation is "terminal" (no more transcript will land) once recording
// has ENDED *and* analysis has committed. We stop polling at that point; the
// SSE stream stays open but is harmless. We treat unknown/missing state as
// non-terminal so we don't prematurely stop live updates.
const TERMINAL_RECORDING_STATES = new Set(["ANALYZED", "COMMITTED"]);
function isTerminalConversation(conv) {
  if (!conv) return false;
  const state = String(
    conv.recording_state ?? conv.recordingState ?? ""
  ).toUpperCase();
  if (!state) return false;
  if (TERMINAL_RECORDING_STATES.has(state)) return true;
  // ENDED + a committed analysis run also counts as terminal.
  if (state === "ENDED") {
    const committed =
      conv.analysis_committed === true ||
      conv.last_analyzed_at != null ||
      conv.analysis_state === "COMMITTED";
    return committed;
  }
  return false;
}

// Estimate a row's pixel height from its utterance length so the
// VariableSizeList lays out without measuring every row up front. The
// renderer wraps at ~the embed width; this is a heuristic (avatar + header +
// padded bubble + wrapped lines). It only needs to be close — react-window
// tolerates estimate drift and we never hold all rows in the DOM anyway.
function estimateRowHeight(turn) {
  const text = turn?.text || "";
  const CHARS_PER_LINE = 42; // narrow iOS embed column
  const lines = Math.max(1, Math.ceil(text.length / CHARS_PER_LINE));
  const BASE = 64; // avatar + role/timestamp header + bubble vertical padding
  const PER_LINE = 22;
  const SPACING = 16; // VStack spacing={4} between rows
  return BASE + lines * PER_LINE + SPACING;
}

// One virtualized row. Renders the SAME `CleanedTurn` the desktop uses (so
// sentence sticky-bracketing highlight + tap-to-seek behave identically),
// optionally preceded by the SAME `SourceMarker` divider when the source
// detail item changes. `react-window` passes an absolute `style` we must
// spread onto the row's outer element.
const Row = React.memo(function Row({ index, style, data }) {
  const {
    allTurns,
    allMeta,
    allSourceIds,
    activeIdx,
    audioStartEpochMs,
    onSeekToMs,
    sourceOrdinalById,
  } = data;
  const turn = allTurns[index];
  const sid = allSourceIds[index];
  const prevSid = index > 0 ? allSourceIds[index - 1] : undefined;
  const showMarker = sid != null && sid !== prevSid;
  const sourceIndex = showMarker ? sourceOrdinalById.get(sid) ?? 0 : 0;

  return (
    <Box style={style} px={4} data-testid="viewer-row" data-row-index={index}>
      {showMarker && <SourceMarker sourceId={sid} sourceIndex={sourceIndex} />}
      <CleanedTurn
        turn={turn}
        meta={allMeta[index]}
        turnIdx={index}
        isActive={index === activeIdx}
        epochOffset={audioStartEpochMs}
        onSeekToMs={onSeekToMs}
      />
    </Box>
  );
});

export default function EmbedConversationViewer({
  // Test seam — production passes none and uses the real EventSource.
  eventSourceFactory = defaultEventSourceFactory,
}) {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const isProfile = searchParams.get("profile") === "1";
  // Temporarily disable embedded audio playback. The iOS app owns native
  // recording surfaces, and this embed should render transcript content only.
  const showAudioPlayer = false;
  const requestedSpeakerFilter = (searchParams.get("speaker") || "")
    .trim()
    .toLowerCase();
  const speakerFilter =
    requestedSpeakerFilter || (!showAudioPlayer ? "employee" : "");
  const employeeOnly = speakerFilter === "employee";

  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // M3 live-growth state. `terminal` gates polling; `liveExtend` carries the
  // growing waveform peaks + duration to the player non-destructively when
  // audio is enabled.
  const [threadId, setThreadId] = useState(null);
  const [terminal, setTerminal] = useState(false);
  const [liveExtend, setLiveExtend] = useState(null);
  const terminalRef = useRef(false);
  useEffect(() => {
    terminalRef.current = terminal;
  }, [terminal]);

  // Audio-player → transcript sync state, hoisted exactly like the desktop
  // ConversationDetailPage so the reused player drives the reused sync logic.
  // In transcript-only mode (`?audio=0`) these stay inert.
  const [currentAudioMs, setCurrentAudioMs] = useState(0);
  const [audioStartEpochMs, setAudioStartEpochMs] = useState(0);
  const audioSeekRef = useRef(null);
  const handleSeekToMs = useCallback((ms) => {
    audioSeekRef.current?.seek?.(ms);
  }, []);
  const handleManifest = useCallback((manifest) => {
    const v = Number(manifest?.audio_start_epoch_ms);
    if (Number.isFinite(v) && v >= 0) setAudioStartEpochMs(v);
  }, []);

  // Profile fixture (memoized so we build the ~1800-turn payload once).
  const profileFixture = useMemo(
    () => (isProfile ? buildProfileFixture() : null),
    [isProfile]
  );

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    if (isProfile && profileFixture) {
      // Profile mode: skip the network, use the synthetic fixture. Static —
      // no SSE, no polling (it's a single, complete 40-min fixture).
      setItems(profileFixture.items);
      setAudioStartEpochMs(PROFILE_AUDIO_START_EPOCH_MS);
      setTerminal(true);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    getConversationDetailItems(conversationId, { type: "CLEANED_TRANSCRIPT" })
      .then((resp) => {
        if (cancelled) return;
        const list =
          resp?.items || (Array.isArray(resp) ? resp : []) || [];
        setItems(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.detail || e?.message || "Failed to load transcript."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, isProfile, profileFixture]);

  // ── M3: discover thread_id + terminal state (drives SSE + poll lifecycle) ────
  useEffect(() => {
    if (isProfile) return undefined;
    let cancelled = false;
    getConversation(conversationId)
      .then((conv) => {
        if (cancelled) return;
        const tid = conv?.thread_id ?? conv?.threadId ?? null;
        if (tid != null) setThreadId(tid);
        setTerminal(isTerminalConversation(conv));
      })
      .catch(() => {
        // A failed metadata fetch just means we can't subscribe to SSE / can't
        // confirm terminal — fall back to polling-as-non-terminal. The static
        // transcript already rendered from the initial load.
        if (!cancelled) setTerminal(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, isProfile]);

  // ── M3: refresh CLEANED items + manifest + peaks, merge by re-flatten ────────
  // Re-fetching the full CLEANED set is its own dedupe — `flattenCleanedTurns`
  // sorts by detail-item index, so existing turns keep stable positions and
  // only new turns land at the end. We also pull fresh peaks + manifest and
  // push them to the player via `liveExtend` so the waveform grows in place.
  const refreshLiveData = useCallback(async () => {
    if (isProfile) return;
    try {
      if (!showAudioPlayer) {
        const resp = await getConversationDetailItems(conversationId, {
          type: "CLEANED_TRANSCRIPT",
        });
        const list = resp?.items || (Array.isArray(resp) ? resp : []) || [];
        setItems(list);
        return;
      }

      const [resp, manifest, peaks] = await Promise.all([
        getConversationDetailItems(conversationId, {
          type: "CLEANED_TRANSCRIPT",
        }),
        getConversationAudioManifest(conversationId).catch(() => null),
        getConversationAudioPeaks(conversationId, 1500).catch(() => null),
      ]);
      const list = resp?.items || (Array.isArray(resp) ? resp : []) || [];
      setItems(list);
      const durationMs = Number(manifest?.total_duration_ms);
      if (peaks?.peaks || Number.isFinite(durationMs)) {
        setLiveExtend({
          peaks: peaks && Array.isArray(peaks.peaks) ? peaks : null,
          durationMs: Number.isFinite(durationMs) ? durationMs : undefined,
        });
      }
      if (manifest) handleManifest(manifest);
    } catch {
      // Transient — the next SSE event or poll tick retries. Don't surface an
      // error over an already-rendered transcript.
    }
  }, [conversationId, isProfile, showAudioPlayer, handleManifest]);

  // ── M3: SSE subscription (preferred trigger) ─────────────────────────────────
  // Re-subscribes on threadId change OR token rotation. Reacts to
  // `cleaned_transcript_ready` scoped to THIS conversation.
  const [tokenVersion, setTokenVersion] = useState(0);
  useEffect(() => {
    const handler = () => setTokenVersion((v) => v + 1);
    if (typeof window !== "undefined") {
      window.addEventListener(TOKEN_REFRESHED_EVENT, handler);
      return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, handler);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (isProfile || threadId == null) return undefined;
    const token = currentEmbedToken?.() || "";
    const url = buildThreadEventsUrl(threadId, token);
    const convIdNum = conversationId != null ? Number(conversationId) : null;

    let es;
    try {
      es = eventSourceFactory(url);
    } catch {
      // SSE unavailable (e.g. DRIFT-8 500 on connect) — the poll fallback
      // below still drives live updates.
      return undefined;
    }
    if (!es) return undefined;

    const onEvent = (rawEvent) => {
      let envelope = null;
      try {
        envelope =
          typeof rawEvent?.data === "string"
            ? JSON.parse(rawEvent.data)
            : rawEvent;
      } catch {
        return;
      }
      if (!envelope || typeof envelope !== "object") return;
      const eventType = envelope.type || envelope.kind || rawEvent?.type;
      const eventPayload = envelope.payload || {};
      const eventConv = envelope.conversation_id ?? eventPayload.conversation_id;
      // Scope to THIS conversation — the per-thread stream carries events for
      // every conversation in the thread.
      if (
        convIdNum != null &&
        eventConv != null &&
        Number(eventConv) !== convIdNum
      ) {
        return;
      }
      if (
        eventType === "cleaned_transcript_ready" ||
        eventType === "recording.transcript_cleaned"
      ) {
        refreshLiveData();
      } else if (
        eventType === "recording_state_changed" ||
        eventType === "recording.finished"
      ) {
        // Re-evaluate the terminal stop condition + grab any final batch.
        getConversation(conversationId)
          .then((conv) => setTerminal(isTerminalConversation(conv)))
          .catch(() => {});
        refreshLiveData();
      }
    };

    if (typeof es.addEventListener === "function") {
      es.addEventListener("message", onEvent);
      LIVE_SSE_EVENTS.forEach((type) => es.addEventListener(type, onEvent));
    } else {
      es.onmessage = onEvent;
    }

    return () => {
      try {
        es.close();
      } catch {
        // ignore
      }
    };
  }, [
    isProfile,
    threadId,
    conversationId,
    tokenVersion,
    eventSourceFactory,
    refreshLiveData,
  ]);

  // ── M3: polling fallback ─────────────────────────────────────────────────────
  // Works regardless of SSE (resilient to DRIFT-8). Runs only while the
  // conversation is non-terminal; stops once it reaches a terminal state.
  useEffect(() => {
    if (isProfile || terminal) return undefined;
    if (typeof window === "undefined") return undefined;
    const id = window.setInterval(() => {
      if (terminalRef.current) return;
      refreshLiveData();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isProfile, terminal, refreshLiveData]);

  // Reuse the desktop flattening + sticky-bracketing active-turn logic.
  const cleanedTurns = useCleanedTurns(
    items,
    showAudioPlayer ? currentAudioMs : -1,
    audioStartEpochMs
  );

  const { allTurns, allMeta, allSourceIds, hasAnyMeta, activeIdx } =
    useMemo(() => {
      if (!employeeOnly) return cleanedTurns;

      const nextTurns = [];
      const nextMeta = [];
      const nextSourceIds = [];
      let nextActiveIdx = -1;
      cleanedTurns.allTurns.forEach((turn, index) => {
        if (String(turn?.role || "").toLowerCase() !== "employee") return;
        if (index === cleanedTurns.activeIdx) {
          nextActiveIdx = nextTurns.length;
        }
        nextTurns.push(turn);
        nextMeta.push(cleanedTurns.allMeta[index]);
        nextSourceIds.push(cleanedTurns.allSourceIds[index]);
      });

      return {
        allTurns: nextTurns,
        allMeta: nextMeta,
        allSourceIds: nextSourceIds,
        hasAnyMeta: nextTurns.length > 0,
        activeIdx: nextActiveIdx,
      };
    }, [
      employeeOnly,
      cleanedTurns,
    ]);

  // Pre-compute "Source #N" ordinals once per turn stream so each row can
  // look up its badge number without scanning. Mirrors the desktop's
  // seenSources accumulation but in a virtualization-safe (index-stable) way.
  const sourceOrdinalById = useMemo(() => {
    const map = new Map();
    let last = undefined;
    let ord = -1;
    for (let i = 0; i < allSourceIds.length; i += 1) {
      const sid = allSourceIds[i];
      if (sid != null && sid !== last) {
        ord += 1;
        if (!map.has(sid)) map.set(sid, ord);
      }
      last = sid;
    }
    return map;
  }, [allSourceIds]);

  // --- react-window plumbing -------------------------------------------------
  const listRef = useRef(null);
  const listOuterRef = useRef(null);
  const sizeMapRef = useRef({});
  const scrollPausedUntilRef = useRef(0);
  const lastScrolledRef = useRef(-1);

  // Stick-to-live-edge bookkeeping. `nearBottomRef` reflects whether the user
  // is at/near the end of the list (chat-log "stick to bottom unless scrolled
  // up"). `prevTurnCountRef` lets us detect an APPEND so we only follow the
  // edge when new turns actually arrived. `scrollMetaRef` caches the most
  // recent scroll geometry react-window reports.
  const nearBottomRef = useRef(true);
  const prevTurnCountRef = useRef(0);
  const scrollMetaRef = useRef({ offset: 0, height: 0 });

  const getItemSize = useCallback(
    (index) => {
      const cached = sizeMapRef.current[index];
      if (cached) return cached;
      const est = estimateRowHeight(allTurns[index]);
      sizeMapRef.current[index] = est;
      return est;
    },
    [allTurns]
  );

  // When the turn stream changes we recompute sizes, but ONLY from the first
  // index whose height could differ. On a pure APPEND the existing prefix is
  // byte-identical, so we reset from the old length forward — that recomputes
  // the new rows without disturbing the cached prefix or the list's pixel
  // scroll offset (which is how react-window preserves scroll position).
  useEffect(() => {
    const prevCount = prevTurnCountRef.current;
    const nextCount = allTurns.length;
    const isAppend = nextCount > prevCount && prevCount > 0;
    if (isAppend) {
      // Drop only the cache entries at/after the boundary (new rows).
      for (let i = prevCount; i < nextCount; i += 1) {
        delete sizeMapRef.current[i];
      }
      listRef.current?.resetAfterIndex?.(prevCount, false);
    } else {
      // Full replace (initial load, profile, or a non-append change).
      sizeMapRef.current = {};
      listRef.current?.resetAfterIndex?.(0, false);
    }
  }, [allTurns]);

  // Pause-on-touch + near-bottom tracking. react-window's onScroll
  // distinguishes programmatic (`scrollUpdateWasRequested: true`) from user
  // scrolls (`false`). A user scroll suspends auto-scroll-to-active AND
  // updates whether we're near the live edge.
  const handleListScroll = useCallback(
    ({ scrollOffset, scrollUpdateWasRequested }) => {
      scrollMetaRef.current = {
        offset: scrollOffset,
        height: scrollMetaRef.current.height,
      };
      const outer = listOuterRef.current;
      if (outer) {
        const distanceFromBottom =
          outer.scrollHeight - (scrollOffset + outer.clientHeight);
        nearBottomRef.current =
          distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
      }
      if (!scrollUpdateWasRequested) {
        scrollPausedUntilRef.current =
          Date.now() + SCROLL_PAUSE_AFTER_USER_INTERACTION_MS;
      }
    },
    []
  );

  // Follow the live edge ONLY on append + when the user was already near the
  // bottom. Otherwise leave them put (don't yank them down). Runs after the
  // size-cache reset effect above so the new content height is realized.
  useEffect(() => {
    const prevCount = prevTurnCountRef.current;
    const nextCount = allTurns.length;
    const isAppend = nextCount > prevCount && prevCount > 0;
    prevTurnCountRef.current = nextCount;
    if (!isAppend) return;
    if (!nearBottomRef.current) return; // user scrolled up → leave them.
    // Jump to the last turn so the freshly-appended content is visible. Use
    // "end" so we settle on the bottom edge, matching the stick-to-bottom feel.
    listRef.current?.scrollToItem?.(nextCount - 1, "end");
  }, [allTurns]);

  // Auto-scroll the active turn to center as playback advances — unless the
  // user recently scrolled (pause) or we already centered this index.
  useEffect(() => {
    if (activeIdx < 0) return;
    if (activeIdx === lastScrolledRef.current) return;
    if (Date.now() < scrollPausedUntilRef.current) return;
    listRef.current?.scrollToItem?.(activeIdx, "center");
    lastScrolledRef.current = activeIdx;
  }, [activeIdx]);

  // Turn-start boundaries for the player's Prev/Next jumps (conv-relative).
  const turnStartsMs = useMemo(() => {
    return allMeta
      .map((m) => Number(m?.start_ms))
      .filter((v) => Number.isFinite(v))
      .map((v) => Math.max(0, v - audioStartEpochMs))
      .sort((a, b) => a - b);
  }, [allMeta, audioStartEpochMs]);

  const rowData = useMemo(
    () => ({
      allTurns,
      allMeta,
      allSourceIds,
      activeIdx,
      audioStartEpochMs,
      onSeekToMs: showAudioPlayer ? handleSeekToMs : undefined,
      sourceOrdinalById,
    }),
    [
      allTurns,
      allMeta,
      allSourceIds,
      activeIdx,
      audioStartEpochMs,
      handleSeekToMs,
      showAudioPlayer,
      sourceOrdinalById,
    ]
  );

  // The virtualized list needs an explicit pixel height. The embed fills the
  // WKWebView; reserve room for the sticky-bottom player (~72px).
  const [listHeight, setListHeight] = useState(0);
  const containerRef = useRef(null);
  useEffect(() => {
    const measure = () => {
      const h =
        (containerRef.current?.clientHeight ||
          (typeof window !== "undefined" ? window.innerHeight : 0)) - 0;
      setListHeight(Math.max(120, h));
    };
    measure();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    return undefined;
  }, []);

  return (
    <EmbedLayout>
      <Box
        display="flex"
        flexDirection="column"
        h="100vh"
        data-testid="embed-conversation-viewer"
        data-profile={isProfile ? "true" : "false"}
        data-terminal={terminal ? "true" : "false"}
        data-audio={showAudioPlayer ? "true" : "false"}
        data-speaker-filter={speakerFilter || "all"}
      >
        <Box ref={containerRef} flex={1} minH={0}>
          {loading && (
            <Center py={8} data-testid="viewer-loading">
              <Spinner size="md" />
            </Center>
          )}

          {!loading && error && (
            <Text color="red.500" px={4} py={3} data-testid="viewer-error">
              {error}
            </Text>
          )}

          {!loading && !error && (!hasAnyMeta || allTurns.length === 0) && (
            <Text color="gray.500" px={4} py={3} data-testid="viewer-empty">
              {employeeOnly ? "No employee transcript yet." : "No transcript yet."}
            </Text>
          )}

          {!loading && !error && hasAnyMeta && allTurns.length > 0 && (
            <VariableSizeList
              ref={listRef}
              outerRef={listOuterRef}
              height={listHeight || 400}
              width="100%"
              itemCount={allTurns.length}
              itemSize={getItemSize}
              itemData={rowData}
              onScroll={handleListScroll}
              overscanCount={6}
              data-testid="viewer-virtual-list"
            >
              {Row}
            </VariableSizeList>
          )}
        </Box>

        {!loading && !error && showAudioPlayer && (
          <ConversationAudioPlayer
            conversationId={isProfile ? "profile" : conversationId}
            turnStartsMs={turnStartsMs}
            onTimeChange={setCurrentAudioMs}
            onManifestLoaded={handleManifest}
            seekRef={audioSeekRef}
            manifestOverride={isProfile ? profileFixture?.manifest : undefined}
            peaksOverride={isProfile ? profileFixture?.peaks : undefined}
            audioUrlOverride={isProfile ? PROFILE_SAMPLE_AUDIO_URL : undefined}
            liveExtend={isProfile ? undefined : liveExtend}
          />
        )}
      </Box>
    </EmbedLayout>
  );
}
